import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import moment from 'moment'
import { Justification } from '../../domain/justification/justification.model'
import { endpoints } from '../../shared/config/endpoints'
import { AdministrationService } from '../administration/administration.service'
import { ApiGateway } from '../../core/remote/api.gateway'
import { TransactionListItem } from '../../domain/transaction/transaction-listitem.model'
import { Transaction } from '../../domain/transaction/transaction.model'
import { JustificationTypes } from '../../domain/justification/justificationtypes.constants'
import { Document } from '../../domain/document/document.model'
import { globals } from '../../shared/config/globals'
import { SegmentHelper } from '../external-services/segment.helper'

@Injectable()
export class DocumentJustificationService {
  constructor(
    private readonly _administrationService: AdministrationService,
    private readonly _apiGateway: ApiGateway,
    private readonly _segment: SegmentHelper,
  ) {}

  getJustificationsById(id: number): Observable<Justification[]> {
    return this.getJustifications({ id } as Document)
  }

  getJustifications(document: Document): Observable<Justification[]> {
    return this.makeDocumentApiCall(document, (p) => this._apiGateway.get(endpoints.justifications.document, p))
  }

  justifyWithTransaction(document: Document, tx: Transaction | TransactionListItem, amount?: number): Observable<Document> {
    const body = {
      type: JustificationTypes.INVOICE,
      transaction_id: tx.id,
      amount: amount,
    }

    return this.makeDocumentApiCall(document, (p, o) => this._apiGateway.post(endpoints.justifications.document, o, p), body)
  }

  justifyAsPrivate(document: Document, description?: string, amount?: number, date?: moment.Moment): Observable<Document> {
    const body = {
      type: JustificationTypes.PRIVATE,
      amount: amount,
      remarks_user: description,
      booking_date: date && date.format(globals.dateFormat),
    }

    if (!body.remarks_user) {
      delete body.remarks_user
    }
    if (!body.booking_date) {
      delete body.booking_date
    }
    if (!body.amount) {
      delete body.amount
    }

    return this.makeDocumentApiCall(document, (p, o) => this._apiGateway.post(endpoints.justifications.document, o, p), body)
  }

  deleteJustification(document: Document, justification: Justification): Observable<Document> {
    this._segment.track('Justification - Deleted', {
      id: justification.id,
      location: 'document',
      'invoice id': justification.invoice ? justification.invoice.id : null,
      'document id': justification.document ? justification.document.id : null,
      'transaction id': justification.transaction ? justification.transaction.id : null,
      'vat declaration id': justification.vat_declaration ? justification.vat_declaration.id : null,
      type: justification.type.toLowerCase().replace('_', ' '),
    })

    return this.makeDocumentApiCall(document, (p, o) => this._apiGateway.delete(endpoints.justifications.document, p, o), null, {
      justificationId: justification.id,
    })
  }

  deleteAllJustifications(document: Document): Observable<Document> {
    return this.makeDocumentApiCall(document, (p, o) => this._apiGateway.delete(endpoints.justifications.document, p, o))
  }

  private makeDocumentApiCall<T>(document: Document, call: (params?: any, object?: any) => Observable<T>, object?: any, params?: any): Observable<T> {
    if (!params) {
      params = {}
    }

    params.documentId = document.id

    return this._administrationService.makeApiCallForDefaultAdministration(call, params, object)
  }
}
